import {
  applyPreset,
  DESIGN_PRESETS,
  designStyle,
  matchingPreset,
  PRESET_DIRECTION_LABEL,
  PRESET_DIRECTIONS,
  SLIDE_DENSITIES,
  SLIDE_STYLES,
  TRAINING_FONTS,
  type DesignPreset,
  type SlideDensity,
  type SlideStyleName,
  type TrainingDesign,
  type TrainingFont,
  type TrainingInstructions,
} from '@allemnii-pulse/shared';
import { cn } from '../../lib/utils.js';
import { Label } from '../../components/ui/Label.js';
import { Textarea } from '../../components/ui/Textarea.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/Select.js';
import { useGoogleFonts } from './SlideView.js';

const COLOR_FIELDS: {
  key: 'primaryColor' | 'accentColor' | 'backgroundColor' | 'textColor';
  label: string;
}[] = [
  { key: 'primaryColor', label: 'Primary' },
  { key: 'accentColor', label: 'Accent' },
  { key: 'backgroundColor', label: 'Background' },
  { key: 'textColor', label: 'Text' },
];

const STYLE_LABEL: Record<SlideStyleName, { name: string; hint: string }> = {
  minimal: { name: 'Minimal', hint: 'No decoration — the content carries the slide' },
  signal: { name: 'Signal', hint: 'An accent card on the title slide, large section numbers' },
  swiss: { name: 'Swiss', hint: 'Rules and an accent square; sections on the accent color' },
  editorial: { name: 'Editorial', hint: 'Hairlines, an outlined circle, regular-weight titles' },
  soft: { name: 'Soft', hint: 'Rounded cards and a row of pills' },
};

const DENSITY_LABEL: Record<SlideDensity, { name: string; hint: string }> = {
  focused: { name: 'Speaker-led', hint: 'One idea per slide in large type; detail in the notes' },
  balanced: { name: 'Balanced', hint: 'A few short points per slide' },
  detailed: { name: 'Reading-first', hint: 'Fuller slides that make sense without a trainer' },
};

/** Every preset's fonts, so the swatches show them. */
const PRESET_FONTS = [
  ...new Set(DESIGN_PRESETS.flatMap((p) => [p.design.headingFont, p.design.bodyFont])),
];

/** A preset as a tiny slide: its title font and colors, its accent, and its name below. */
function PresetSwatch({
  preset,
  selected,
  disabled,
  onSelect,
}: {
  preset: DesignPreset;
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
}) {
  const { design } = preset;
  return (
    <button
      type="button"
      title={preset.description}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex flex-col gap-1 rounded-lg border-2 p-1 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-not-allowed disabled:opacity-60',
        selected ? 'border-accent' : 'border-transparent hover:border-border',
      )}
    >
      <span
        className="flex aspect-video w-full flex-col justify-end gap-1 overflow-hidden rounded border border-border p-2"
        style={{ background: design.backgroundColor }}
      >
        <span className="h-1 w-5 rounded-full" style={{ background: design.accentColor }} />
        <span
          className="text-lg font-bold leading-none"
          style={{ fontFamily: `'${design.headingFont}'`, color: design.primaryColor }}
        >
          Aa
        </span>
      </span>
      <span className="truncate px-0.5 text-xs font-medium">{preset.name}</span>
    </button>
  );
}

function FontSelect({
  id,
  value,
  disabled,
  onChange,
}: {
  id: string;
  value: TrainingFont;
  disabled?: boolean;
  onChange: (font: TrainingFont) => void;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as TrainingFont)} disabled={disabled}>
      <SelectTrigger id={id}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {TRAINING_FONTS.map((font) => (
          <SelectItem key={font} value={font}>
            {font}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** The design settings: heading/body fonts, four colors and free-text "other details". */
export function DesignFields({
  idPrefix,
  value,
  disabled,
  onChange,
}: {
  idPrefix: string;
  value: TrainingDesign;
  disabled?: boolean;
  onChange: (design: TrainingDesign) => void;
}) {
  const set = <K extends keyof TrainingDesign>(key: K, v: TrainingDesign[K]) =>
    onChange({ ...value, [key]: v });
  useGoogleFonts(PRESET_FONTS);
  const current = matchingPreset(value);
  const style = designStyle(value);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-medium">Preset</span>
          <span className="text-xs text-muted">
            {current ? current.description : 'Custom design — pick a preset to start over'}
          </span>
        </div>
        <div className="flex flex-col gap-2">
          {PRESET_DIRECTIONS.map((direction) => (
            <div key={direction} className="flex flex-col gap-0.5">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">
                {PRESET_DIRECTION_LABEL[direction]}
              </span>
              <div className="grid grid-cols-3 gap-1 sm:grid-cols-4">
                {DESIGN_PRESETS.filter((p) => p.direction === direction).map((preset) => (
                  <PresetSwatch
                    key={preset.id}
                    preset={preset}
                    selected={current?.id === preset.id}
                    disabled={disabled}
                    onSelect={() => onChange(applyPreset(value, preset))}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-style`}>Slide style</Label>
        <Select
          value={style}
          onValueChange={(v) => set('style', v as SlideStyleName)}
          disabled={disabled}
        >
          <SelectTrigger id={`${idPrefix}-style`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SLIDE_STYLES.map((s) => (
              <SelectItem key={s} value={s}>
                {STYLE_LABEL[s].name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted">{STYLE_LABEL[style].hint}</span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-heading-font`}>Heading font</Label>
          <FontSelect
            id={`${idPrefix}-heading-font`}
            value={value.headingFont}
            disabled={disabled}
            onChange={(f) => set('headingFont', f)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-body-font`}>Body font</Label>
          <FontSelect
            id={`${idPrefix}-body-font`}
            value={value.bodyFont}
            disabled={disabled}
            onChange={(f) => set('bodyFont', f)}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {COLOR_FIELDS.map(({ key, label }) => (
          <div key={key} className="flex flex-col gap-1.5">
            <Label htmlFor={`${idPrefix}-${key}`}>{label}</Label>
            <div className="flex h-8 items-center gap-2 rounded-lg border border-border px-2">
              <input
                id={`${idPrefix}-${key}`}
                type="color"
                value={value[key]}
                disabled={disabled}
                onChange={(e) => set(key, e.target.value)}
                className="h-5 w-6 cursor-pointer rounded border-0 bg-transparent p-0 disabled:cursor-not-allowed"
              />
              <span className="font-mono text-xs text-muted">{value[key]}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-other`}>Other design details</Label>
        <Textarea
          id={`${idPrefix}-other`}
          rows={3}
          maxLength={2000}
          value={value.otherDetails}
          disabled={disabled}
          placeholder="Layout preferences, imagery style, how dense slides should be…"
          onChange={(e) => set('otherDetails', e.target.value)}
        />
      </div>
    </div>
  );
}

/** Generation instructions (the training's style and tone) plus the design settings. */
export function InstructionsFields({
  idPrefix,
  value,
  disabled,
  onChange,
}: {
  idPrefix: string;
  value: TrainingInstructions;
  disabled?: boolean;
  onChange: (instructions: TrainingInstructions) => void;
}) {
  const density = (value.density as SlideDensity | undefined) ?? 'balanced';
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-instructions`}>Generation instructions</Label>
        <Textarea
          id={`${idPrefix}-instructions`}
          rows={4}
          maxLength={4000}
          value={value.generationInstructions}
          disabled={disabled}
          placeholder="The style of the training — e.g. technical and precise, light-hearted with humour, formal…"
          onChange={(e) => onChange({ ...value, generationInstructions: e.target.value })}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-density`}>Slide density</Label>
        <Select
          value={density}
          onValueChange={(v) => onChange({ ...value, density: v as SlideDensity })}
          disabled={disabled}
        >
          <SelectTrigger id={`${idPrefix}-density`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SLIDE_DENSITIES.map((d) => (
              <SelectItem key={d} value={d}>
                {DENSITY_LABEL[d].name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted">{DENSITY_LABEL[density].hint}</span>
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium">Design</span>
        <DesignFields
          idPrefix={idPrefix}
          value={value.design}
          disabled={disabled}
          onChange={(design) => onChange({ ...value, design })}
        />
      </div>
    </div>
  );
}
